import type {
  IProgramId,
  IProgramEnrolment,
} from '@app/domain/program/program.interface'
import type { AuthenticatedRequest } from '@app/rest/rest.lib'
import { authenticatedRequest } from '@app/rest/rest.lib'
import type { IApiPatient } from '@app/state/patients/helpers'

import type {
  IPatient,
  IPatientCommunicationConsent,
} from './patient.interface'

export type IPatientResponse = {
  patient: IApiPatient
}

export type IPatientProgramsResponse = {
  patientEnrolments: IProgramEnrolment[]
}

export type ISendInteractionToIntakeAnywhereResponse = {
  patientId: string
  programInteractionId: string
}

export function getPatient(
  patientId: string,
): AuthenticatedRequest<IPatientResponse> {
  const url = `/api/v2/patient/${patientId}`

  return authenticatedRequest<IPatientResponse>({
    method: 'get',
    url,
  })
}

export function getProgramEnrolments(patientId: string) {
  const url = `/api/v2/patient/${patientId}/enroled-programs`
  return authenticatedRequest<IPatientProgramsResponse>({
    method: 'get',
    url,
  })
}

export type IPatientCommunicationConsentResponse = {
  communicationConsent: IPatientCommunicationConsent
}

export function getPatientCommunicationConsent(
  patientId: string,
): AuthenticatedRequest<IPatientCommunicationConsentResponse> {
  const url = `/api/v2/patient/${patientId}/communication-consent`
  return authenticatedRequest<IPatientCommunicationConsentResponse>({
    method: 'get',
    url,
  })
}

export function updatePatientEmailConsent(
  patientId: string,
  consentsToEmail: boolean,
): AuthenticatedRequest<IPatientCommunicationConsentResponse> {
  const url = `/api/v2/patient/${patientId}/communication-consent/email`
  return authenticatedRequest<IPatientCommunicationConsentResponse>({
    method: 'post',
    url,
    data: { consentsToEmail },
  })
}

export function removePatientSmsConsent(
  patientId: string,
): AuthenticatedRequest<IPatientCommunicationConsentResponse> {
  const url = `/api/v2/patient/${patientId}/communication-consent/sms`
  return authenticatedRequest<IPatientCommunicationConsentResponse>({
    method: 'delete',
    url,
  })
}

export function sendInteractionToIntakeAnywhere(
  patientId: string,
  programInteractionId: string,
) {
  const url = `/api/v2/program/transmitToIntakeAnywhere`
  return authenticatedRequest<ISendInteractionToIntakeAnywhereResponse>({
    method: 'post',
    url,
    data: { patientId, programInteractionId },
  })
}

export type IRevokeRemoteInteractionResponse = {
  status: string
  message?: string
}

export function revokeRemoteInteraction(
  patientId: string,
  programInteractionId: string,
) {
  return authenticatedRequest<IRevokeRemoteInteractionResponse>({
    method: 'post',
    url: `/api/v2/program/interaction/${programInteractionId}/patient/${patientId}/revokeRemote`,
  })
}

export type IPatientsResponse = {
  status: string
  patients: IPatient[]
  pageCount: number
  totalNumberOfRecords: number
}

export type IOptions = {
  birthSex?: string[]
  dateOfBirth?: string
  mrn?: string
  patientName?: string
  sortDirection?: string
  sortField?: string
  inProgramId?: IProgramId
  notInProgramId?: IProgramId
}

export function getPatients(
  pageNumber: number,
  recordsPerPage: number,
  options: IOptions,
): AuthenticatedRequest<IPatientsResponse> {
  const {
    birthSex,
    dateOfBirth,
    mrn,
    patientName,
    sortDirection,
    sortField,
    inProgramId,
    notInProgramId,
  } = options
  const paramArray: string[][] = [
    ['pageNumber', pageNumber.toString()],
    ['recordsPerPage', recordsPerPage.toString()],
    ['dateOfBirth', dateOfBirth],
    ['mrn', mrn],
    ['patientName', patientName],
    ['sortDirection', sortDirection],
    ['sortField', sortField],
    ['inProgramId', inProgramId],
    ['notInProgramId', notInProgramId],
  ].filter((it) => {
    return it[1] !== undefined && it[1] !== null
  })

  const urlParams = new URLSearchParams(paramArray).toString()

  let birthSexUrlParams = ''
  if (birthSex && birthSex.length > 0) {
    birthSexUrlParams = birthSex.map((it) => `&birthSex=${it}`).join('')
  }

  const url = `/api/v2/patients?${urlParams}${birthSexUrlParams}`

  return authenticatedRequest<IPatientsResponse>({
    method: 'get',
    url,
  })
}

export type IPatientImportRow = {
  mrn: string
  givenNames: string
  familyName: string
  dateOfBirth: string
  birthSex: string
  emailAddress?: string
  phoneNumberType?: string
  phoneNumber?: string
}

export type IPatientImportErrorType =
  | 'duplicate'
  | 'failure-to-persist-patient'
  | 'failure-to-persist-contact-information'
  | 'invalid-birth-sex'
  | 'invalid-date-of-birth'
  | 'invalid-phone-type'
  | 'invalid-phone-number'
  | 'invalid-email'
  | 'invalid-mrn'
  | 'invalid-given-names'
  | 'invalid-family-name'
  | 'parsing-error'

export type IPatientImportError = {
  type: IPatientImportErrorType
  rowIndex: number
  row?: IPatientImportRow
}

export type IUploadPatientImportFileResponse = {
  errors: IPatientImportError[]
  numberAdded: number
  numberOfPatientsAddedToPrograms: Record<string, number>
  stage: 'validation' | 'import'
  status: 'success' | 'failure'
}

export function uploadPatientImportFile(
  file: File,
  selectedPrograms: string[],
): AuthenticatedRequest<IUploadPatientImportFileResponse> {
  const url = `/api/v2/patient/bulk-upload`
  const formData = new FormData()
  formData.set(
    'selectedPrograms',
    JSON.stringify(selectedPrograms, undefined, 2),
  )
  formData.append('csv', file, file.name)

  return authenticatedRequest<IUploadPatientImportFileResponse>({
    method: 'post',
    url,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
}
